import React, { useState, useRef } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { IconPhoto, IconX, IconPlus, IconEdit } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import ImageCropModal from './ImageCropModal';
import useImageUpload from './useImageUpload';
import './MultiImageUpload.css';

/**
 * MultiImageUpload - Component for uploading and managing multiple images
 * @param {Object} props
 * @param {Array} props.images - Array of image URLs
 * @param {Function} props.onChange - Callback when images change
 * @param {number} props.maxImages - Maximum number of images (default: 4)
 * @param {boolean} props.allowCrop - Allow cropping images
 * @param {string} props.uploadFolder - Cloudinary folder
 * @param {Object} props.maxSize - Max dimensions
 * @param {string} props.cropShape - Crop shape ('rect' or 'round')
 * @param {number} props.aspectRatio - Crop aspect ratio
 */
const MultiImageUpload = ({
  images = [],
  onChange,
  maxImages = 4,
  allowCrop = true,
  uploadFolder = 'user_photos/posts',
  maxSize = { width: 1600, height: 1600 },
  cropShape = 'rect',
  aspectRatio = 0
}) => {
  const fileInputRef = useRef(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  
  const {
    isUploading,
    uploadProgress,
    previewUrls,
    showCropModal,
    imageToCrop,
    cropConfig,
    handleFileSelection,
    uploadFiles,
    handleCropComplete,
    closeCropModal,
    reset
  } = useImageUpload({
    uploadFolder,
    maxSize,
    allowMultiple: true,
    requireCrop: allowCrop,
    cropShape,
    aspectRatio,
    onSuccess: (urls) => {
      const newImages = Array.isArray(urls) ? urls : [urls];
      
      if (editingIndex !== null) {
        // Replace existing image
        const updatedImages = [...images];
        updatedImages[editingIndex] = newImages[0];
        onChange(updatedImages);
        setEditingIndex(null);
      } else {
        // Add new images
        const combinedImages = [...images, ...newImages];
        onChange(combinedImages.slice(0, maxImages));
      }
      
      reset();
    },
    onError: (error) => {
      console.error('Upload error:', error);
      setEditingIndex(null);
      reset();
    }
  });

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files || []);
    
    if (editingIndex === null && images.length + files.length > maxImages) {
      toast.error(`Maximum ${maxImages} images allowed`);
      e.target.value = '';
      return;
    }
    
    if (editingIndex !== null && files.length > 1) {
      toast.error('Select only one image to replace');
      e.target.value = '';
      return;
    }
    
    if (handleFileSelection(files)) {
      if (!allowCrop) {
        uploadFiles(files);
      }
    }
    
    e.target.value = '';
  };

  const handleAddImages = () => {
    setEditingIndex(null);
    fileInputRef.current?.click();
  };

  const handleEditImage = (index) => {
    setEditingIndex(index);
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    onChange(updatedImages);
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    if (dragCounter <= 1) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    setDragCounter(0);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      if (images.length + imageFiles.length > maxImages) {
        toast.error(`Maximum ${maxImages} images allowed`);
        return;
      }
      
      if (handleFileSelection(imageFiles)) {
        if (!allowCrop) {
          uploadFiles(imageFiles);
        }
      }
    } else {
      toast.error('Please drop image files only');
    }
  };

  const canAddMore = (images.length > 0 || previewUrls.length > 0) && images.length < maxImages && !isUploading;

  return (
    <Box 
      className={`multi-image-upload ${isDragging ? 'dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple={editingIndex === null}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      {/* Image grid */}
      <Box className="image-grid">
        {/* Existing images */}
        {images.map((imageUrl, index) => (
          <Box key={index} className="image-item">
            <Box className="image-container">
              <img 
                src={imageUrl} 
                alt={`Upload ${index + 1}`}
                className="image-preview"
              />
              
              {/* Image controls */}
              {!isUploading && (
                <Box className="image-controls">
                  <IconButton
                    size="small"
                    className="control-btn edit-btn"
                    onClick={() => handleEditImage(index)}
                    title="Replace image"
                  >
                    <IconEdit size={14} />
                  </IconButton>
                  <IconButton
                    size="small"
                    className="control-btn remove-btn"
                    onClick={() => handleRemoveImage(index)}
                    title="Remove image"
                  >
                    <IconX size={14} />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        ))}

        {/* Preview images (during upload) */}
        {previewUrls.map((previewUrl, index) => (
          <Box key={`preview-${index}`} className="image-item uploading">
            <Box className="image-container">
              <img 
                src={previewUrl} 
                alt={`Uploading ${index + 1}`}
                className="image-preview"
              />
              
              {/* Upload progress overlay */}
              <Box className="upload-overlay">
                <Box className="upload-progress">
                  <Box 
                    className="progress-bar"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </Box>
                <Typography variant="caption" className="progress-text">
                  {uploadProgress < 100 ? `${uploadProgress}%` : 'Processing...'}
                </Typography>
              </Box>
            </Box>
          </Box>
        ))}

        {/* Add more button */}
        {canAddMore && (
          <Box className="image-item add-more">
            <Box 
              className="add-container"
              onClick={handleAddImages}
              role="button"
              tabIndex={0}
            >
              <IconPlus size={24} className="add-icon" />
              <Typography variant="caption" className="add-text">
                Add Image
              </Typography>
              <Typography variant="caption" className="image-count">
                {images.length}/{maxImages}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {/* Initial upload button (when no images) */}
      {images.length === 0 && previewUrls.length === 0 && (
        <Box 
          className="initial-upload"
          onClick={handleAddImages}
          role="button"
          tabIndex={0}
          sx={{ 
            opacity: isUploading ? 0.6 : 1,
            pointerEvents: isUploading ? 'none' : 'auto'
          }}
        >
          <IconPhoto size={32} className="upload-icon" />
          <Typography variant="body2" className="upload-text">
            {isDragging ? 'Drop images here' : 'Click to add images or drag & drop'}
          </Typography>
          <Typography variant="caption" className="upload-hint">
            Up to {maxImages} images • JPG, PNG, WebP
          </Typography>
        </Box>
      )}

      {/* Crop Modal */}
      {showCropModal && imageToCrop && (
        <ImageCropModal
          key={`multi-crop-${imageToCrop.name}-${Date.now()}`}
          isOpen={showCropModal}
          onClose={closeCropModal}
          imageFile={imageToCrop}
          cropShape={cropConfig.cropShape}
          aspectRatio={cropConfig.aspectRatio}
          autoUpload={false}
          onCroppedBlob={handleCropComplete}
          uploadFolder={cropConfig.uploadFolder}
          maxSize={cropConfig.maxSize}
        />
      )}
    </Box>
  );
};

export default MultiImageUpload;
